import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { base44 } from "api/apiClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { AlertTriangle, Search } from "lucide-react";
import { parseLocaleNumber } from "utils";

function parseLocalizedNumber(value) {
  return parseLocaleNumber(value, null);
}

function roundToCents(value) {
  return Math.round(value * 100) / 100;
}

export default function CostGapDialog({ recipe, inventoryItems, recipes, open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [resolutions, setResolutions] = useState({});
  const [searchByGap, setSearchByGap] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (recipe) {
      const initial = {};
      (recipe.cost_gaps || []).forEach((gap) => {
        initial[gap.ingredient_index] = {
          mode: gap.kind === "stub_no_cost" ? "price" : "link",
          linkSource: "inventory",
        };
      });
      setResolutions(initial);
      setSearchByGap({});
      setError(null);
    }
  }, [recipe?.id]);

  const inventoryWithCost = useMemo(
    () =>
      (inventoryItems || []).filter(
        (it) => it.cost_per_unit != null && Number(it.cost_per_unit) > 0
      ),
    [inventoryItems]
  );

  // Recipes (preps or menu items) that produce an inventory item — these can
  // act as the cost source for a missing-cost ingredient. Skip the recipe
  // being edited so users can't link it to itself.
  const prepRecipes = useMemo(
    () =>
      (recipes || []).filter(
        (r) => r.output_inventory_item_id && r.id !== recipe?.id
      ),
    [recipes, recipe?.id]
  );

  if (!recipe) return null;
  const gaps = recipe.cost_gaps || [];

  const setRes = (idx, patch) =>
    setResolutions((prev) => ({ ...prev, [idx]: { ...(prev[idx] || {}), ...patch } }));

  const filteredInventory = (search) => {
    if (!search) return inventoryWithCost;
    const q = search.toLowerCase();
    return inventoryWithCost.filter((it) => (it.name || "").toLowerCase().includes(q));
  };

  const filteredProducts = (search) => {
    if (!search) return prepRecipes;
    const q = search.toLowerCase();
    return prepRecipes.filter((r) => (r.name || "").toLowerCase().includes(q));
  };

  const isResolved = (gap) => {
    const res = resolutions[gap.ingredient_index] || {};
    if (res.mode === "link") return !!res.inventoryItemId;
    if (res.mode === "price") {
      const cost = parseLocalizedNumber(res.cost);
      const packQty = parseLocalizedNumber(res.packQty);
      return cost != null && cost >= 0 && packQty != null && packQty > 0;
    }
    return false;
  };

  const anyResolved = gaps.some(isResolved);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // 1. Recipe ingredient updates (link)
      const linkUpdates = gaps
        .filter((gap) => {
          const res = resolutions[gap.ingredient_index] || {};
          return res.mode === "link" && res.inventoryItemId;
        })
        .map((gap) => ({
          index: gap.ingredient_index,
          inventoryItemId: resolutions[gap.ingredient_index].inventoryItemId,
        }));

      if (linkUpdates.length > 0) {
        const newIngredients = (recipe.ingredients || []).map((ing, i) => {
          const upd = linkUpdates.find((u) => u.index === i);
          if (!upd) return ing;
          const inv = (inventoryItems || []).find((x) => x.id === upd.inventoryItemId);
          const fallbackName = (recipes || []).find(
            (r) => r.output_inventory_item_id === upd.inventoryItemId
          )?.output_inventory_item_name;
          return {
            ...ing,
            inventory_item_id: upd.inventoryItemId,
            inventory_item_name: inv?.name || fallbackName || ing.inventory_item_name,
          };
        });
        await base44.entities.Recipe.update(recipe.id, { ingredients: newIngredients });
      }

      // 2. Inventory price updates (stub_no_cost → price)
      for (const gap of gaps) {
        const res = resolutions[gap.ingredient_index] || {};
        if (
          res.mode === "price" &&
          gap.inventory_item_id &&
          isResolved(gap)
        ) {
          const cost = parseLocalizedNumber(res.cost);
          const packQty = parseLocalizedNumber(res.packQty);
          await base44.entities.InventoryItem.update(gap.inventory_item_id, {
            purchase_unit_cost: roundToCents(cost),
            purchase_unit_quantity: packQty,
          });
        }
      }

      queryClient.invalidateQueries({ queryKey: ["recipes"] });
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const isLinkProducts = (res) => res.mode === "link" && res.linkSource === "product";
  const isLinkInventory = (res) => res.mode === "link" && (res.linkSource || "inventory") === "inventory";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            {t("recipes.costGapDialogTitle", { recipe: recipe.name })}
          </DialogTitle>
          <DialogDescription>{t("recipes.costGapDialogDescription")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          {gaps.map((gap) => {
            const res = resolutions[gap.ingredient_index] || {};
            const search = searchByGap[gap.ingredient_index] || "";
            const linkSource = res.linkSource || "inventory";
            const options =
              linkSource === "product" ? filteredProducts(search) : filteredInventory(search);
            return (
              <div
                key={gap.ingredient_index}
                className="rounded-lg border border-slate-200 bg-slate-50 p-3"
              >
                <div className="flex flex-wrap items-start justify-between gap-x-2 gap-y-1 mb-2">
                  <div className="font-medium text-slate-900 min-w-0 truncate">
                    {gap.name || `#${gap.ingredient_index + 1}`}
                  </div>
                  <span className="text-xs text-slate-500 shrink-0">
                    {gap.kind === "unlinked"
                      ? t("recipes.costGapTooltipUnlinked")
                      : t("recipes.costGapTooltipNoCost")}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-2">
                  {gap.kind === "stub_no_cost" && (
                    <Button
                      type="button"
                      size="sm"
                      variant={res.mode === "price" ? "default" : "outline"}
                      onClick={() => setRes(gap.ingredient_index, { mode: "price" })}
                    >
                      {t("recipes.costGapSetPrice")}
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant={isLinkProducts(res) ? "default" : "outline"}
                    onClick={() =>
                      setRes(gap.ingredient_index, {
                        mode: "link",
                        linkSource: "product",
                        inventoryItemId: undefined,
                      })
                    }
                  >
                    {t("recipes.costGapPickFromProducts")}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={isLinkInventory(res) ? "default" : "outline"}
                    onClick={() =>
                      setRes(gap.ingredient_index, {
                        mode: "link",
                        linkSource: "inventory",
                        inventoryItemId: undefined,
                      })
                    }
                  >
                    {t("recipes.costGapPickFromInventory")}
                  </Button>
                </div>

                {res.mode === "price" && gap.kind === "stub_no_cost" && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">{t("recipes.costGapPriceLabel")}</Label>
                      <Input
                        type="text"
                        inputMode="decimal"
                        placeholder="0.00"
                        value={res.cost ?? ""}
                        onChange={(e) =>
                          setRes(gap.ingredient_index, { cost: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{t("recipes.costGapPackQtyLabel")}</Label>
                      <Input
                        type="text"
                        inputMode="decimal"
                        placeholder="1"
                        value={res.packQty ?? ""}
                        onChange={(e) =>
                          setRes(gap.ingredient_index, { packQty: e.target.value })
                        }
                      />
                    </div>
                  </div>
                )}

                {res.mode === "link" && (
                  <div>
                    <div className="relative mb-1">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                      <Input
                        className="pl-7 h-8 text-sm"
                        placeholder={
                          linkSource === "product"
                            ? t("recipes.costGapSearchProductsPlaceholder")
                            : t("recipes.costGapSearchPlaceholder")
                        }
                        value={search}
                        onChange={(e) =>
                          setSearchByGap((prev) => ({
                            ...prev,
                            [gap.ingredient_index]: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-white">
                      {options.length === 0 ? (
                        <div className="px-2 py-2 text-xs text-slate-500">
                          {t("recipes.costGapNoMatch")}
                        </div>
                      ) : linkSource === "product" ? (
                        options.slice(0, 50).map((r) => {
                          const targetInvId = r.output_inventory_item_id;
                          const selected = res.inventoryItemId === targetInvId;
                          return (
                            <button
                              type="button"
                              key={r.id}
                              onClick={() =>
                                setRes(gap.ingredient_index, { inventoryItemId: targetInvId })
                              }
                              className={`w-full text-left px-2 py-1.5 text-sm hover:bg-slate-100 flex items-center justify-between border-b last:border-b-0 ${
                                selected ? "bg-amber-50" : ""
                              }`}
                            >
                              <span className="truncate">{r.name}</span>
                              <span className="text-xs text-slate-500 ml-2 shrink-0 truncate max-w-[40%]">
                                {r.output_inventory_item_name || ""}
                              </span>
                            </button>
                          );
                        })
                      ) : (
                        options.slice(0, 50).map((it) => {
                          const selected = res.inventoryItemId === it.id;
                          return (
                            <button
                              type="button"
                              key={it.id}
                              onClick={() =>
                                setRes(gap.ingredient_index, { inventoryItemId: it.id })
                              }
                              className={`w-full text-left px-2 py-1.5 text-sm hover:bg-slate-100 flex items-center justify-between border-b last:border-b-0 ${
                                selected ? "bg-amber-50" : ""
                              }`}
                            >
                              <span className="truncate">{it.name}</span>
                              <span className="text-xs text-slate-500 ml-2 shrink-0">
                                €{Number(it.cost_per_unit || 0).toFixed(2)}/{it.unit || ""}
                              </span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-3">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            {t("recipes.cancel")}
          </Button>
          <Button type="button" onClick={handleSave} disabled={!anyResolved || saving}>
            {saving ? t("recipes.costGapSaving") : t("recipes.costGapSaveAll")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
